import { useContext, useEffect, useRef, useState } from 'react';
import convertRequestStateToSearchParams from '../../services/convertRequestStateToSearchParams';
import { isValidEmail } from '../../services/emailUtils';
import { requestResetPassword } from '../../veevaIdApi';
import ResponseStatus from '../../constants/ResponseStatus';
import PasswordResetConfirmation from './PasswordResetConfirmation';
import PasswordResetForm from './PasswordResetForm';
import useNetworkErrorNotification from '../../services/useNetworkErrorNotification';
import PageContainer from '../../components/PageContainer';
import useRequestState from '../../services/useRequestState';
import useMessageService from '../../services/i18n/useMessageService';
import LoadingScreenContext from '../../services/LoadingScreenContext';
import {
    getErrorMessage,
    getFirstError,
    showErrorNotificationFromResponse,
} from '../../services/errorUtils';
import UnauthenticatedRouteLanguageContext from '../../services/UnauthenticatedRouteLanguageContext';
import ResponseErrorTypes from '../../constants/ResponseErrorTypes';
import RecaptchaV2Invisible from '../../components/RecaptchaV2Invisible';

function ForgotPasswordPage() {
    const [
        [
            confirmationSentTitle = 'Confirmation Sent',
            forgotPasswordTitle = 'Forgot Password?',
            invalidEmailError = 'Invalid email format entered',
        ],
    ] = useMessageService([
        'forgot_password.confirmation_sent_title',
        'forgot_password.label_with_question_mark',
        'general.invalid_email',
    ]);
    const { setLoading } = useContext(LoadingScreenContext);
    const [email, setEmail] = useState('');
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const { showNotification } = useNetworkErrorNotification();
    const requestState = useRequestState();
    const requestStateParams = convertRequestStateToSearchParams(requestState);
    const resetCaptcha = useRef();
    const executeCaptcha = useRef();

    const setExecuteRef = (callback) => {
        executeCaptcha.current = callback;
    };

    const setResetRef = (callback) => {
        resetCaptcha.current = callback;
    };
    const { messageLanguage } = useContext(UnauthenticatedRouteLanguageContext);

    useEffect(() => {
        setErrorMessage('');
    }, [messageLanguage]);

    const handleResetPassword = async () => {
        if (!isValidEmail(email)) {
            setErrorMessage(invalidEmailError);
            return;
        }

        setErrorMessage('');
        const token = await executeCaptcha.current?.();
        setLoading(true);
        const response = await requestResetPassword(email, token, requestState);
        setLoading(false);
        resetCaptcha.current?.();
        switch (response.responseStatus) {
            case ResponseStatus.SUCCESS:
                setShowConfirmation(true);
                break;
            case ResponseStatus.FAILURE: {
                const responseError = getFirstError(response);
                switch (responseError.type) {
                    case ResponseErrorTypes.VEEVAID_INVALID_RECAPTCHA: {
                        const message = await getErrorMessage(responseError, messageLanguage);
                        setErrorMessage(message);
                        break;
                    }
                    default: {
                        const i18nOptions = { language: messageLanguage };
                        await showErrorNotificationFromResponse(response, i18nOptions);
                    }
                }
                break;
            }
            case ResponseStatus.UI_NETWORK_ERROR:
                await showNotification();
                break;
            default:
            // No-op
        }
    };

    const handleInputChange = (value) => {
        setEmail(value);
        setErrorMessage(''); // clear error as user types new email
    };

    const containerTitle = showConfirmation ? confirmationSentTitle : forgotPasswordTitle;

    return (
        <PageContainer title={containerTitle}>
            <RecaptchaV2Invisible reset={setResetRef} execute={setExecuteRef} />
            {showConfirmation ? (
                <PasswordResetConfirmation email={email} />
            ) : (
                <PasswordResetForm
                    onResetPassword={handleResetPassword}
                    onInputChange={handleInputChange}
                    email={email}
                    emailErrorMessage={errorMessage}
                    requestStateParams={requestStateParams}
                />
            )}
        </PageContainer>
    );
}

export default ForgotPasswordPage;
